import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';

import { BUTTON_SIZES, Button } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalFooter, ModalHeader } from 'ui/modal';
import { InputText } from 'ui/inputs';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';

import { CREATE_TILESET_MODAL } from 'modules/workflows/constants';
import { closeModal, deselectNodes } from 'modules/workflows/ducks';
import { Edge, Node } from 'modules/workflows/types';

import { createTilesetMutation } from './tilesets.graphql';
import { TilesetPreview } from './TilesetPreview';
import { parseForTilesetPreview, parseForCreateTileset } from './utils';
import {
  FragmentDetails,
  FormField,
  StyledBody,
  StyledLabel,
  StyledTextArea,
  TilesetText,
} from './styles';

export type CreateTilesetModalProps = {
  /**  Edges to create tileset */
  selectedEdges: Edge[];
  /**  Nodes to create tileset */
  selectedNodes: Node[];
  /** Whether the modal is open */
  isOpen: boolean;
};

export const CREATE_ACTION_ID = 'create-tileset';
export const TILESET_TEXT = 'tileset-text';

export const CreateTilesetModal = ({
  isOpen,
  selectedNodes,
  selectedEdges,
}: CreateTilesetModalProps) => {
  const dispatch = useDispatch();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const { parsedNodesForDisplay, parsedEdgesForDisplay } = useMemo(
    () =>
      parseForTilesetPreview({
        nodes: selectedNodes,
        edges: selectedEdges,
      }),
    [selectedNodes, selectedEdges],
  );

  const { parsedNodes, parsedNotes, parsedEdges } = useMemo(
    () =>
      parseForCreateTileset({
        nodes: selectedNodes,
        edges: selectedEdges,
      }),
    [selectedNodes, selectedEdges],
  );

  const handleCloseModal = () => dispatch(closeModal(CREATE_TILESET_MODAL));

  const [createFragment, { loading }] = useMutation(createTilesetMutation, {
    variables: {
      name,
      description,
      fragment: { nodes: parsedNodes, edges: parsedEdges, notes: parsedNotes },
    },
    onCompleted: () => {
      dispatch(addPositiveToast('Journey tileset created!'));
      dispatch(deselectNodes(selectedNodes.map((x) => x.id)));
      handleCloseModal();
    },
    onError: () => {
      dispatch(
        addNegativeToastWithErrorId('Journey tileset could not be created.', 'createTileSet'),
      );
    },
  });

  return (
    <Modal
      id={CREATE_TILESET_MODAL}
      isModalOpen={isOpen}
      onClose={handleCloseModal}
      size={MODAL_SIZES.LARGE}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>New tileset</ModalHeader>
      <StyledBody>
        <FragmentDetails>
          <FormField>
            <StyledLabel>Name</StyledLabel>
            <InputText
              id="input-text-fragment"
              value={name}
              onChange={(val: string) => setName(val)}
              isReadOnly={loading}
            />
          </FormField>
          <FormField>
            <StyledLabel>
              Description <span>(optional)</span>
            </StyledLabel>
            <StyledTextArea
              id="input-text-area-fragment"
              value={description}
              onChange={(val: string) => setDescription(val)}
              placeholder="Write a helpful note..."
              isReadOnly={loading}
            />
          </FormField>
          <TilesetText data-test={TILESET_TEXT}>
            Tileset contains {selectedNodes.length} tile{selectedNodes.length > 1 && 's'}
          </TilesetText>
        </FragmentDetails>

        <TilesetPreview
          {...{
            nodes: parsedNodesForDisplay,
            edges: parsedEdgesForDisplay,
          }}
        />
      </StyledBody>
      <ModalFooter>
        <Button
          isDisabled={!name || loading}
          autoFocus
          data-test={CREATE_ACTION_ID}
          onClick={() => createFragment()}
          size={BUTTON_SIZES.LARGE}
        >
          Create tileset
        </Button>
      </ModalFooter>
    </Modal>
  );
};
